<template>
  <master-layout pageTitle="Your Applications">
    <DeveloperPortal v-if="applications.length == 0" />
    <div v-if="applications.length > 0">
      <div class="gridMargin">
        <div class="search-bar-create-application">
          <SearchBar
            v-model="searchQuery"
            placeholder="Search application"
            :rightIcon="true"
            @search="filterApplications"
            class="search-bar"
          />
          <VButton connotation="cta" appearance="filled" label="Create a new application" @click="$router.push('/developer-portal/applications/create')" > </VButton>
        </div>
        <AppTable
          :columns="columns"
          :rows="sortedApplications"
          :sort-field="sortField"
          :sort-direction="sortDirection"
          @sort="
            ({ field, direction }) => {
              sortField = field;
              sortDirection = direction;
            }
          "
          :rowClick="goToAppDetails"
        >
          <template #app_name="{ value }">
            <div class="url-row">
              <span class="url-text" :title="value">
                {{ value }}
              </span>
            </div>
          </template>
          <template #app_uuid="{ value }">
            <div class="url-row">
              <span class="url-text" :title="value">
                {{ value }}
              </span>
              <VButton
                class="copy-btn"
                appearance="ghost"
                connotation="cta"
                size="condensed"
                @click.stop="() => copyToClipboard(value)"
              >
                <template #icon>
                  <VIcon :name="copyOutline" />
                </template>
              </VButton>
            </div>
          </template>
          <template #status="{ value }">
            <span class="status-chip" :style="getChipStyle(value)">
              {{ value.charAt(0).toUpperCase() + value.slice(1) }}
            </span>
          </template>
          <template #app_description="{ value }">
            <div class="url-row">
              <span class="url-text" :title="value">
                {{ value }}
              </span>
            </div>
          </template>
          <template #created_at="{ value }">
            {{ dateFormatter(value) }}
          </template>
          <template #actions="{ row }">
            <div class="icon-wrapper">
              <div class="tooltip">
                <router-link
                  :to="`/developer-portal/applications/${row.id}`"
                  class="action-link"
                  @click.stop
                >
                  <VIcon :name="eyeOutline" class="icon-style"></VIcon>
                </router-link>
                <span class="tooltip-text">View Details</span>
              </div>

              <div class="tooltip">
                <VIcon
                  :name="backspaceOutline"
                  class="icon-style revokeIcon"
                  @click.stop="openRevokeAppModal(row)"
                ></VIcon>
                <span class="tooltip-text">{{
                  row.status === "active" ? "Revoke" : "Activate"
                }}</span>
              </div>

              <div class="tooltip">
                <VIcon
                  :name="trashOutline"
                  class="icon-style deleteIcon"
                  @click.stop="openDeleteAppModal(row.id)"
                ></VIcon>
                <span class="tooltip-text">Delete Application</span>
              </div>
            </div>
          </template>
        </AppTable>
      </div>
      <CustomWarningModal
        :isOpen="showDeleteAppModal"
        heading="Are you sure you want to delete this app?"
        description="Once deleted, all API keys, secrets, and linked configurations will be lost. Any connected integrations will stop working."
        cancelText="Cancel"
        confirmText="Delete"
        @cancel="closeDeleteAppModal"
        @confirm="confirmDeleteApp"
      />
      <CustomWarningModal
        :isOpen="showRevokeAppModal"
        :heading="
          selectedRow?.status === 'active'
            ? 'Are you sure you want to revoke this app?'
            : 'Are you sure you want to activate this app?'
        "
        :description="
          selectedRow?.status === 'active'
            ? 'Once revoked, all API keys, secrets, and linked configurations will be disabled. Any connected integrations will stop working.'
            : 'Activating this app will restore its functionality and allow integrations to work again.'
        "
        cancelText="Cancel"
        :confirmText="selectedRow?.status === 'active' ? 'Revoke' : 'Activate'"
        @cancel="closeRevokeAppModal"
        @confirm="updateAppStatus"
      />
    </div>
  </master-layout>
</template>

<script>
import AppTable from "@/components/CustomTable.vue";
import apiInterceptor from "@/shared/apiInterceptor";
import DeveloperPortal from "./developer-portal.vue";
import { updateAppStatusHelper, deleteAppHelper } from "@/helpers/apps.helper";
import CustomWarningModal from "@/components/CustomWarningModal.vue";
import { getChipStyle, dateFormatter } from "@/helpers/global.helpers";
import { useUIStore } from '@/stores/ui';
import { useClipboard } from '@/composables/useClipboard';
import SearchBar from '@/components/SearchBar.vue';
import { environment } from '@/shared/env.js';
export default {
  name: "AppsList",
  components: {
    AppTable,
    DeveloperPortal,
    CustomWarningModal,
    SearchBar,
  },
  setup() {
    const { copyToClipboard } = useClipboard();
    return { copyToClipboard };
  },
  data() {
    return {
      addOutline: 'plus-line',
      eyeOutline: 'eye-line',
      backspaceOutline: 'backspace-line',
      trashOutline: 'delete-line',
      copyOutline: 'copy-2-line',
      showDeleteAppModal: false,
      showRevokeAppModal: false,
      selectedAppId: null,
      selectedRow: null,
      searchQuery: "",
      columns: [
        { label: "Name", field: "app_name", sortable: true },
        { label: "Application ID", field: "app_uuid" },
        { label: "Status", field: "status" },
        { label: "Description", field: "app_description" },
        { label: "Date Created", field: "created_at", sortable: true },
        { label: "Created By", field: "created_by" },
        { label: "Action", field: "actions" },
      ],
      applications: [],
      filteredApplications: [],
      sortField: "created_at",
      sortDirection: "desc",
    };
  },
  watch: {
    searchQuery(val) {
      if (!val || val.trim() === '') {
        this.filteredApplications = this.applications;
      } else {
        this.filterApplications();
      }
    },
  },
  async ionViewWillEnter() {
    await this.fetchAppsList();
    this.searchQuery = "";
  },
  computed: {
    sortedApplications() {
      if (!this.sortField || !this.sortDirection)
        return this.filteredApplications;

      const sortKey = this.sortField;
      const multiplier = this.sortDirection === "asc" ? 1 : -1;

      return [...this.filteredApplications].sort((a, b) => {
        const aVal = a[sortKey];
        const bVal = b[sortKey];

        // DATE SORTING (ISO or string)
        const aDate = aVal ? new Date(aVal) : null;
        const bDate = bVal ? new Date(bVal) : null;

        if (aDate && bDate && !isNaN(aDate) && !isNaN(bDate)) {
          if (aDate < bDate) return -1 * multiplier;
          if (aDate > bDate) return 1 * multiplier;
          return 0;
        }

        // STRING SORTING (dictionary-style)
        const aStr = String(aVal ?? "");
        const bStr = String(bVal ?? "");

        return (
          aStr.localeCompare(bStr, "en", {
            sensitivity: "base",
            numeric: true,
          }) * multiplier
        );
      });
    },
  },
  methods: {
    getChipStyle,
    dateFormatter,
    openDeleteAppModal(appId) {
      this.selectedAppId = appId;
      this.showDeleteAppModal = true;
    },
    closeDeleteAppModal() {
      this.selectedAppId = null;
      this.showDeleteAppModal = false;
    },
    openRevokeAppModal(row) {
      this.selectedRow = row;
      this.showRevokeAppModal = true;
    },
    closeRevokeAppModal() {
      this.selectedRow = null;
      this.showRevokeAppModal = false;
    },
    goToAppDetails(row) {
      this.$router.push(`/developer-portal/applications/${row.id}`);
    },
    filterApplications() {
      const query = this.searchQuery.trim().toLowerCase();
      if (!query) {
        this.filteredApplications = this.applications;
        return;
      }
      this.filteredApplications = this.applications.filter(
        (app) =>
          app.app_name.toLowerCase().includes(query) ||
          app.app_uuid.toLowerCase().includes(query) ||
          app.created_by.toLowerCase().includes(query) ||
          app.app_description.toLowerCase().includes(query)
      );
    },
    async fetchAppsList() {
      try {
        useUIStore().updateLoading({ isLoading: true, message: 'Loading applications...' });
        const appsList = await apiInterceptor.get(
          environment("VUE_APP_APIGEE_API_URL") + "apps"
        );
        this.applications = appsList.apps || [];
        this.filteredApplications = [...this.applications];
      } catch (error) {
        useUIStore().updateToast({
          open: true,
          message: error?.response?.data?.detail || "Something went wrong!",
          type: "danger",
        });
      } finally {
        useUIStore().updateLoading({ isLoading: false });
      }
    },

    async confirmDeleteApp() {
      const appId = this.selectedAppId;
      this.closeDeleteAppModal();
      useUIStore().updateLoading({ isLoading: true, message: 'Deleting application...' });
      try {
        await deleteAppHelper({
          appId: appId,
          environment: environment,
          onSuccess: async () => {
            useUIStore().updateToast({
              open: true,
              message: "App deleted successfully!",
              type: "success",
            });
            await this.fetchAppsList();
          },
          onError: (error) => {
            useUIStore().updateToast({
              open: true,
              message: error.message || "Something went wrong!",
              type: "danger",
            });
          },
        });
      } finally {
        useUIStore().updateLoading({ isLoading: false });
      }
    },
    async updateAppStatus() {
      const appId = this.selectedRow.id;
      const currentStatus = this.selectedRow.status.toLowerCase();
      const newStatus = currentStatus === "active" ? "revoke" : "active";
      this.closeRevokeAppModal();
      useUIStore().updateLoading({ isLoading: true, message: 'Updating application status...' });
      try {
        await updateAppStatusHelper({
          appId: appId,
          environment: environment,
          action: newStatus,
          onSuccess: async () => {
            useUIStore().updateToast({
              open: true,
              message: `App ${
                newStatus === "active" ? "activated" : "revoked"
              } updated successfully!`,
              type: "success",
            });
            await this.fetchAppsList();
          },
          onError: (error) => {
            useUIStore().updateToast({
              open: true,
              message: error.message || "Something went wrong!",
              type: "danger",
            });
          },
        });
      } finally {
        useUIStore().updateLoading({ isLoading: false });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.search-bar-create-application {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  gap: 10px;
}
.search-bar {
  width: 300px;
}
.vivid-input {
  background: var(--white);
  border-radius: 5px;
  min-height: 40px;
}
.action-link {
  color: var(--purple);
  font-weight: 700;
  text-decoration: none;
}
.status-chip {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
}
@media (max-width: 576px) {
  .search-bar-create-application {
    flex-direction: column;
    justify-content: center;
    align-items: stretch;
  }
  .search-bar {
    width: 100%;
  }
}

.icon-wrapper {
  display: flex;
  gap: 8px;
}
.icon-style {
  font-size: 20px;
  cursor: pointer;
}
.deleteIcon {
  color: var(--orange);
}

.revokeIcon {
  color: var(--gray-04);
}

.tooltip {
  position: relative;
  display: inline-block;
}

.tooltip-text {
  visibility: hidden;
  width: max-content;
  background-color: #333;
  color: #fff;
  text-align: center;
  border-radius: 6px;
  padding: 4px 8px;
  position: absolute;
  z-index: 1001;
  bottom: 100%;
  left: 100%;
  transform: translateX(-50%);
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
  font-size: 12px;
  white-space: nowrap;
}

.tooltip:hover .tooltip-text {
  visibility: visible;
  opacity: 1;
}

.url-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.url-text {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.copy-btn {
  flex-shrink: 0;
}
</style>
